// Added by Sejune Oh on 2020-02-20
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { Component, createRef } from 'react'
import { observer } from 'mobx-react'
import { observable, action, computed, makeObservable } from 'mobx'
import styled from 'styled-components'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import Logger from '@/helper/Logger'
import Intl from '@/helper/Intl'
import { ReactEmptyEvent, ReactClickEvent, KeyIndexedObject } from '@/helper/define'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { UserBaseCProps } from '../../../UserBase/UserBaseContainer'
import { CollapsibleForm } from '@/components/common/CollapsibleForm'
import CustomInfo from './CustomInfo'
import { CustomInfoTable, DisplableUserCustomInfoProp } from '../CustomInfoTable'
import { UserCustomInfoProp } from '@/stores/mgmt/user/UserStore'
import { reqGetPredefinedUserCustomKeys } from '@/stores/dataStorage/config/dsConfig.service'
import { IconButton } from '@/components/_primes/v2'
import { DeleteIcon } from '@/components/_primes/icons'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Interface
interface CustomInfoCProps extends UserBaseCProps {}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
@observer
class CustomInfoContainer extends Component<CustomInfoCProps> {
  mounted: boolean = false
  customInfoElement?: UserCustomInfoProp
  customInfoList: Array<DisplableUserCustomInfoProp> = []
  customInfoIndex: number = 0
  isErrorOccured: boolean = false
  menuItems: string[] = []
  selectedCustomInfoKeys: KeyIndexedObject<string | null> = {}

  _customInfoTableRef = createRef<CustomInfoTable>()
  _customInfoDialogRef = createRef<any>()

  constructor(props: CustomInfoCProps) {
    super(props)
    makeObservable(this, {
      mounted: observable,
      customInfoElement: observable,
      customInfoList: observable,
      customInfoIndex: observable,
      isErrorOccured: observable,
      menuItems: observable,
      selectedCustomInfoKeys: observable,
      componentDidMount: action,
      getMenuItems: action,
      _handleDialogConfirm: action,
      _addCustomInfo: action,
      _changeCustomInfo: action,
      _setCustomColumnElement: action,
      _initCustomInfoList: action,
      _updateCustomInfoList: action,
      _setCustomInfoList: action,
      _handleDeleteCustomInfo: action,
      visibleMenuItems: computed,
    })
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  componentDidMount = async () => {
    try {
      await this.getMenuItems()
      if (this.props.userId) {
        await this._updateCustomInfoList()
      }
      this.mounted = true
      this.props.onChildComponentMount('UserCustomInfo')
    } catch (err) {
      Logger.errLog(err.response ? err.response.data.message : err.message)
    }
  }

  render() {
    if (this.isErrorOccured) return null

    const title = Intl.getIntlText({ id: 'htmljs_react.mgmt.custominfocontainer.title' })
    return (
      this.mounted && (
        <CollapsibleForm title={title}>
          <CustomInfo
            customInfoDialogRef={this._customInfoDialogRef}
            customInfoTableRef={this._customInfoTableRef}
            customInfoList={this.customInfoList}
            customInfoElement={this.customInfoElement}
            onAddCustomInfoBtnClick={this._handleAddCustomInfoBtnClick}
            onDialogConfirm={this._handleDialogConfirm}
            onRowClick={this._handleRowClick}
            menuItems={this.visibleMenuItems}
          />
        </CollapsibleForm>
      )
    )
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  get visibleMenuItems() {
    const filteredMenuItems = this.menuItems.filter((v) => !this.selectedCustomInfoKeys[v])
    if (!this.customInfoElement) return filteredMenuItems

    return [...filteredMenuItems, this.customInfoElement.key]
  }

  async getMenuItems() {
    const {
      data: { custom_keys },
    } = await reqGetPredefinedUserCustomKeys()
    this.menuItems = custom_keys
  }

  _handleDialogConfirm = ({
    customInfoElement,
    err,
  }: {
    customInfoElement: UserCustomInfoProp | null
    err: Error | null
  }): Promise<{}> => {
    return new Promise(async (resolve, reject) => {
      if (err) {
        this.props.showSnackbar!('err', err)
        return reject(err)
      }

      if (customInfoElement!.key === 'NULL') return

      if (!this.customInfoElement) {
        await this._addCustomInfo(customInfoElement!)
        this.customInfoIndex += 1
      } else {
        await this._changeCustomInfo(customInfoElement!)
      }

      if (this._customInfoTableRef.current) this._customInfoTableRef.current.getData()

      resolve(true)
    })
  }

  _addCustomInfo = async ({ id, ...customInfoElement }: UserCustomInfoProp) => {
    let customInfoId = id || `r${this.customInfoIndex}`
    this.customInfoList = [
      ...this.customInfoList,
      {
        ...customInfoElement,
        id: customInfoId,
        index: this.customInfoIndex,
        delete_btn: (
          <DeleteBtnWrapper>
            <IconButton
              size='small'
              title={Intl.getIntlText({ id: 'htmljs_react.mgmt.custominfocontainer.btn_delete' })}
              onClick={this._handleDeleteCustomInfo.apply(null, [this.customInfoIndex])}
            >
              <DeleteIcon />
            </IconButton>
          </DeleteBtnWrapper>
        ),
      },
    ]
    this.selectedCustomInfoKeys[customInfoElement.key] = customInfoElement.value
  }

  _changeCustomInfo = async (customInfoElement: UserCustomInfoProp) => {
    this.customInfoElement = {
      ...this.customInfoElement,
      ...customInfoElement,
    }

    this.customInfoList = this.customInfoList.map((element) => {
      if (element.id !== this.customInfoElement!.id) return element
      return this.customInfoElement!
    })

    this.selectedCustomInfoKeys[customInfoElement.key] = customInfoElement.value
  }

  _setCustomColumnElement = (curId?: string) => {
    if (curId) this.customInfoElement = this.customInfoList.find(({ id }) => id === curId)
    else this.customInfoElement = undefined
  }

  _initCustomInfoList = (customInfoList: UserCustomInfoProp[]) => {
    const displableCustomInfoList: Array<DisplableUserCustomInfoProp> = []
    this.customInfoIndex = customInfoList!.length

    customInfoList.map((customInfoElement, index) => {
      displableCustomInfoList.push({
        ...customInfoElement,
        id: customInfoElement.id! || `r${index}`,
        index,
        delete_btn: (
          <DeleteBtnWrapper>
            <IconButton
              size='small'
              title={Intl.getIntlText({ id: 'htmljs_react.mgmt.custominfocontainer.btn_delete' })}
              onClick={this._handleDeleteCustomInfo.apply(null, [index])}
            >
              <DeleteIcon />
            </IconButton>
          </DeleteBtnWrapper>
        ),
      } as DisplableUserCustomInfoProp)
      this.selectedCustomInfoKeys[customInfoElement.key] = customInfoElement.value
      return index
    })

    return displableCustomInfoList
  }

  _updateCustomInfoList = () => {
    this.customInfoList = []
    this._setCustomInfoList(this._initCustomInfoList(this.props.userElement!.user_custom_infos!))
  }

  _setCustomInfoList = (customInfoList: Array<UserCustomInfoProp>) => {
    this.customInfoList = customInfoList
  }

  _handleDeleteCustomInfo = (customInfoIndex: number): ReactClickEvent => async (event) => {
    event.stopPropagation()

    const elemIndex = this.customInfoList.findIndex(({ index }) => index === customInfoIndex)
    const customInfoElement = this.customInfoList[elemIndex]
    const customInfoList = [
      ...this.customInfoList.slice(0, elemIndex),
      ...this.customInfoList.slice(elemIndex + 1, this.customInfoList.length),
    ]

    if (customInfoElement!.id!.indexOf('r') !== 0) {
      // TODO > REMOVE
    } else {
      await this._setCustomInfoList(customInfoList)
      if (this._customInfoTableRef.current) this._customInfoTableRef.current.getData()
    }

    this.selectedCustomInfoKeys[customInfoElement.key] = null
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  _handleAddCustomInfoBtnClick: ReactEmptyEvent = async () => {
    await this._setCustomColumnElement()
    this._customInfoDialogRef.current!.openDialog()
  }

  _handleRowClick: ReactClickEvent = async ({ currentTarget }) => {
    const curId = currentTarget.dataset.id
    await this._setCustomColumnElement(curId)
    this._customInfoDialogRef.current!.openDialog()
  }

  getFormData = () => {
    return [...this.customInfoList.map(({ key, value }) => ({ key, value }))]
  }
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
const DeleteBtnWrapper = styled.span`
  && {
    display: flex;
    width: 100%;
    height: 100%;
    justify-content: flex-end;
  }
`
export default CustomInfoContainer

